import React, { useEffect, useMemo, useState } from 'react';
import { fetchMyTasks } from '../../services/assignments';
import { AttachmentList, TaskReplies } from '../../components/LearningTasks/TaskReplies';
import '../../components/LearningTasks/LearningTasks.css';

const STATUS_ORDER = ['Assigned', 'Submitted', 'Graded'];

function formatDate(value) {
  if (!value) return null;
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function GradesTable({ grades }: any) {
  if (!grades?.length) return null;
  return (
    <table className="lt-grades" aria-label="Grades">
      <thead>
        <tr><th scope="col">Standard</th><th scope="col">Grade</th></tr>
      </thead>
      <tbody>
        {grades.map((g) => (
          <tr key={g.mlsCode}>
            <td title={g.mlsCode}>{g.friendlyTitle || g.mlsCode}</td>
            <td>
              <span className={`lt-grade lt-grade-${g.score == null ? 'none' : 'scored'}`} data-color={g.color || undefined}>
                {g.label}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TaskCard({ task, isFamily }: any) {
  const [open, setOpen] = useState<any>(false);
  const [replyCount, setReplyCount] = useState(task.replyCount || 0);
  const released = Boolean(task.gradesReleased);
  const statusClass = `lt-status lt-status-${task.studentStatus.toLowerCase().replace(/\s+/g, '-')}`;
  const firstName = (task.studentName || 'your child').split(' ')[0];
  let toggleLabel;
  if (isFamily) {
    toggleLabel = open ? 'Hide graded work' : `Graded work & comments${replyCount ? ` (${replyCount})` : ''}`;
  } else {
    toggleLabel = open ? 'Hide submission' : `Submission & conversation${replyCount ? ` (${replyCount})` : ''}`;
  }
  return (
    <article className="lt-card" aria-label={task.title}>
      <div className="lt-card-head">
        <div>
          <h3 className="lt-card-title">{task.title || 'Learning task'}</h3>
          <div className="lt-card-meta">
            {isFamily && <span>👤 {task.studentName}</span>}
            {(task.className || task.subject) && <span>🏫 {task.className || task.subject}</span>}
            {task.type && <span>{task.type}</span>}
            {task.dueDate && <span>📅 Due {formatDate(task.dueDate)}</span>}
          </div>
        </div>
        <span className={statusClass}>{task.studentStatus}</span>
      </div>
      {task.instructions && <p className="lt-instructions">{task.instructions}</p>}
      {task.standards?.length > 0 && !released && (
        <div className="lt-standards">
          {task.standards.map((std) => (
            <span key={std.mlsCode} className="lt-standard" title={std.mlsCode}>{std.friendlyTitle || std.mlsCode}</span>
          ))}
        </div>
      )}
      {released && <GradesTable grades={task.grades} />}
      <AttachmentList attachments={task.attachments} taskId={task.id} />
      {isFamily && !released ? (
        <p className="lt-muted lt-readonly-note">
          {firstName}’s submission and grades will be shared here once the teacher finishes grading.
        </p>
      ) : (
        <button
          type="button"
          className="lt-btn lt-card-toggle"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          💬 {toggleLabel}
        </button>
      )}
      {open && !isFamily && (
        <TaskReplies
          taskId={task.id}
          studentId={task.studentId}
          title="Submission & conversation with your teacher"
          emptyText="Nothing sent yet. Send your work or ask your teacher a question."
          onPosted={() => setReplyCount((count) => count + 1)}
        />
      )}
      {open && isFamily && released && (
        <>
          <TaskReplies
            taskId={task.id}
            studentId={task.studentId}
            canReply={false}
            title={`${firstName}’s graded submission`}
            emptyText={`${firstName} did not send any work through Academic Eye for this task.`}
            readOnlyText="Shared for reference. This conversation is between the student and the teacher."
          />
          <TaskReplies
            taskId={task.id}
            studentId={task.studentId}
            channel="family"
            title="Your comments to the teacher"
            emptyText="Have something to share about this work? Send the teacher a comment."
            onPosted={() => setReplyCount((count) => count + 1)}
          />
        </>
      )}
    </article>
  );
}

/**
 * Learning tasks teachers published to the signed-in student, or to a family's linked children.
 */
export default function MyAssignments({ audience = 'student' }: any) {
  const [tasks, setTasks] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [studentFilter, setStudentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let active = true;
    fetchMyTasks()
      .then((rows) => { if (active) setTasks(rows); })
      .catch((e) => { if (active) setError(e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const students = useMemo(() => {
    const map = new Map();
    tasks.forEach((t) => map.set(String(t.studentId), t.studentName));
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [tasks]);

  const visible = tasks.filter((t) =>
    (studentFilter === 'all' || String(t.studentId) === studentFilter)
    && (statusFilter === 'all' || t.studentStatus === statusFilter));

  const isFamily = audience === 'family';
  return (
    <div className="lt-page">
      <header className="lt-page-header">
        <h1>Assignments</h1>
        <p className="lt-muted">
          {isFamily
            ? 'Learning tasks your children’s teachers have shared. Grades and graded work appear once the teacher finishes grading, and you can comment to the teacher from there.'
            : 'Learning tasks your teachers have shared with you. Send your work or ask your teacher a question; your grades appear once the teacher finishes grading.'}
        </p>
      </header>
      {!loading && !error && tasks.length > 0 && (
        <div className="lt-filters">
          {isFamily && students.length > 1 && (
            <select aria-label="Filter by child" value={studentFilter} onChange={(e) => setStudentFilter(e.target.value)}>
              <option value="all">All children</option>
              {students.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
          <select aria-label="Filter by status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            {STATUS_ORDER.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
        </div>
      )}
      {loading && <p className="lt-muted" role="status">Loading assignments...</p>}
      {error && <p role="alert" className="lt-error">Could not load assignments: {error}</p>}
      {!loading && !error && tasks.length === 0 && <p className="lt-muted">No assignments have been shared yet.</p>}
      {!loading && !error && tasks.length > 0 && visible.length === 0 && <p className="lt-muted">No assignments match these filters.</p>}
      <div className="lt-cards">
        {visible.map((task) => (
          <TaskCard key={`${task.id}-${task.studentId}`} task={task} isFamily={isFamily} />
        ))}
      </div>
    </div>
  );
}
